// Service for Google Maps and Places API
export interface ParkingSpot {
  place_id: string
  name: string
  address: string
  latitude: number
  longitude: number
  rating?: number
  price_level?: number
  opening_hours?: {
    open_now?: boolean
    weekday_text?: string[]
  }
  photos?: string[]
  distance?: number
}

export interface UserLocation {
  latitude: number
  longitude: number
}

export class MapsService {
  private apiKey: string
  private map: google.maps.Map | null = null

  constructor() {
    this.apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
  }

  async loadGoogleMaps(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.google && window.google.maps) {
        resolve()
        return
      }

      // Check if already loading
      if ((window as any).googleMapsLoading) {
        // Wait for the existing loading promise
        ;(window as any).googleMapsLoading.then(resolve).catch(reject)
        return
      }

      // Check if API key is available
      if (!this.apiKey || this.apiKey === 'undefined') {
        reject(new Error('Google Maps API key not found. Please check your environment variables.'))
        return
      }

      // Check if script already exists
      const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`)
      if (existingScript) {
        // Script exists but Google Maps not ready yet, wait a bit
        const checkReady = () => {
          if (window.google && window.google.maps) {
            resolve()
          } else {
            setTimeout(checkReady, 100)
          }
        }
        checkReady()
        return
      }

      // Create loading promise
      const loadingPromise = new Promise<void>((resolveLoading, rejectLoading) => {
        const script = document.createElement('script')
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places`
        script.async = true
        script.defer = true
        
        script.onload = () => {
          delete (window as any).googleMapsLoading
          resolveLoading()
        }
        
        script.onerror = () => {
          delete (window as any).googleMapsLoading
          rejectLoading(new Error('Failed to load Google Maps. Please check your API key and internet connection.'))
        }
        
        document.head.appendChild(script)
      })

      // Store the loading promise
      ;(window as any).googleMapsLoading = loadingPromise
      
      loadingPromise.then(resolve).catch(reject)
    })
  }

  initializeMap(element: HTMLElement, center: UserLocation): google.maps.Map {
    const map = new google.maps.Map(element, {
      center: { lat: center.latitude, lng: center.longitude },
      zoom: 15,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ],
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      gestureHandling: 'greedy', // Allow one-finger navigation on mobile
    })

    this.map = map
    // Remove PlacesService initialization to avoid deprecated API warnings
    // this.service = new google.maps.places.PlacesService(map)
    return map
  }

  async getCurrentLocation(): Promise<UserLocation & { method?: string }> {
    // Try high accuracy GPS first
    try {
      const location = await this.getHighAccuracyLocation()
      console.log('High accuracy location obtained')
      return { ...location, method: 'GPS (High Accuracy)' }
    } catch (error) {
      console.warn('High accuracy location failed:', error)
    }

    // Try network-based location
    try {
      const location = await this.getNetworkLocation()
      console.log('Network-based location obtained')
      return { ...location, method: 'Network/WiFi' }
    } catch (error) {
      console.warn('Network-based location failed:', error)
    }

    // Try IP-based location
    try {
      const location = await this.getIPLocation()
      console.log('IP-based location obtained')
      return { ...location, method: 'IP Geolocation' }
    } catch (error) {
      console.warn('IP-based location failed:', error)
    }

    // Final fallback to New York
    console.warn('All location methods failed, using default location (New York)')
    return {
      latitude: 40.7128,
      longitude: -74.0060,
      method: 'Default (New York)'
    }
  }

  private async getHighAccuracyLocation(): Promise<UserLocation> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'))
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
        },
        (error) => {
          reject(new Error(`High accuracy geolocation failed: ${error.message}`))
        },
        {
          enableHighAccuracy: true,
          timeout: 15000, // Longer timeout for GPS
          maximumAge: 300000, // 5 minutes
        }
      )
    })
  }

  private async getNetworkLocation(): Promise<UserLocation> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'))
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
        },
        (error) => {
          reject(new Error(`Network geolocation failed: ${error.message}`))
        },
        {
          enableHighAccuracy: false, // Use network/WiFi positioning
          timeout: 10000,
          maximumAge: 600000, // 10 minutes
        }
      )
    })
  }

  private async getIPLocation(): Promise<UserLocation> {
    try {
      // Try multiple IP geolocation services
      const services = [
        'https://ipapi.co/json/',
        'https://ip-api.com/json/',
        'https://ipinfo.io/json'
      ]

      for (const service of services) {
        try {
          const response = await fetch(service)
          if (!response.ok) continue
          
          const data = await response.json()
          
          // Handle different API response formats
          let lat: number, lon: number
          
          if (service.includes('ipapi.co')) {
            lat = data.latitude
            lon = data.longitude
          } else if (service.includes('ip-api.com')) {
            lat = data.lat
            lon = data.lon
          } else if (service.includes('ipinfo.io')) {
            const [latStr, lonStr] = data.loc.split(',')
            lat = parseFloat(latStr)
            lon = parseFloat(lonStr)
          } else {
            continue
          }

          if (lat && lon && !isNaN(lat) && !isNaN(lon)) {
            return {
              latitude: lat,
              longitude: lon
            }
          }
        } catch (error) {
          console.warn(`IP location service ${service} failed:`, error)
          continue
        }
      }
      
      throw new Error('All IP location services failed')
    } catch (error) {
      throw new Error(`IP geolocation failed: ${error}`)
    }
  }

  async requestLocationPermission(): Promise<boolean> {
    try {
      // Check if permissions API is available
      if ('permissions' in navigator) {
        const permission = await navigator.permissions.query({ name: 'geolocation' })
        return permission.state === 'granted'
      }
      
      // Fallback: try to get location to test permission
      await this.getHighAccuracyLocation()
      return true
    } catch (error) {
      return false
    }
  }

  getMapBounds(map: google.maps.Map): { center: UserLocation; radius: number } {
    const bounds = map.getBounds()
    if (!bounds) {
      return { center: { latitude: 40.7128, longitude: -74.0060 }, radius: 2000 }
    }

    const center = bounds.getCenter()
    const ne = bounds.getNorthEast()

    // Calculate the radius as the distance from center to corner
    const radius = this.calculateDistance(
      center.lat(),
      center.lng(),
      ne.lat(),
      ne.lng()
    )

    // Limit radius to 10km maximum
    const limitedRadius = Math.min(radius, 10000)

    return {
      center: { latitude: center.lat(), longitude: center.lng() },
      radius: limitedRadius
    }
  }

  async searchParkingsInBounds(map: google.maps.Map, userLocation?: UserLocation): Promise<ParkingSpot[]> {
    const { center, radius } = this.getMapBounds(map)
    
    // Always use map center for search to show parking spots where the map is centered
    const parkingSpots = await this.searchNearbyParkings(center, radius)
    
    // Calculate distances from user location if available, otherwise from map center
    const referenceLocation = userLocation || center
    parkingSpots.forEach(spot => {
      spot.distance = this.calculateDistance(
        referenceLocation.latitude,
        referenceLocation.longitude,
        spot.latitude,
        spot.longitude
      )
    })
    
    // Sort by distance from reference location
    return parkingSpots.sort((a, b) => (a.distance || 0) - (b.distance || 0))
  }

  async searchNearbyParkings(location: UserLocation, radius: number = 2000): Promise<ParkingSpot[]> {
    return new Promise((resolve) => {
      if (!this.map) {
        console.error('Map not initialized')
        resolve([])
        return
      }

      // Use Google Places Service (client-side)
      const service = new google.maps.places.PlacesService(this.map)
      
      const request: google.maps.places.PlaceSearchRequest = {
        location: new google.maps.LatLng(location.latitude, location.longitude),
        radius: Math.min(radius, 50000), // Max 50km for API
        type: 'parking'
      }

      service.nearbySearch(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          // Convert Google Places results to our ParkingSpot format
          const parkingSpots: ParkingSpot[] = results.map((place) => ({
            place_id: place.place_id || '',
            name: place.name || 'Unnamed Parking',
            address: place.vicinity || place.formatted_address || 'Address not available',
            latitude: place.geometry?.location?.lat() || 0,
            longitude: place.geometry?.location?.lng() || 0,
            rating: place.rating,
            price_level: place.price_level,
            // Note: opening_hours.open_now is deprecated, would need getDetails() for current status
            opening_hours: place.opening_hours ? {} : undefined,
            photos: place.photos ? place.photos.slice(0, 3).map((photo) => 
              photo.getUrl({ maxWidth: 400, maxHeight: 300 })
            ) : undefined,
          }))

          // Calculate distances
          parkingSpots.forEach(spot => {
            spot.distance = this.calculateDistance(
              location.latitude,
              location.longitude,
              spot.latitude,
              spot.longitude
            )
          })

          // Filter by radius and sort by distance
          const filteredSpots = parkingSpots
            .filter(spot => (spot.distance || 0) <= radius)
            .sort((a, b) => (a.distance || 0) - (b.distance || 0))

          resolve(filteredSpots)
        } else {
          console.error('Places search failed:', status)
          resolve([])
        }
      })
    })
  }


  async getPlaceDetails(placeId: string): Promise<ParkingSpot | null> {
    // For now, return null since we're using mock data
    // This method would be implemented with the new Places API in production
    console.log('getPlaceDetails called for:', placeId)
    return null
  }

  addMarker(map: google.maps.Map, position: UserLocation, title: string, isUser: boolean = false): google.maps.Marker {
    return new google.maps.Marker({
      position: { lat: position.latitude, lng: position.longitude },
      map: map,
      title: title,
      icon: isUser ? {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="8" fill="#00B04F" stroke="white" stroke-width="2"/>
            <circle cx="12" cy="12" r="3" fill="white"/>
          </svg>
        `),
        scaledSize: new google.maps.Size(24, 24),
      } : {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#00B04F"/>
            <circle cx="12" cy="9" r="2.5" fill="white"/>
          </svg>
        `),
        scaledSize: new google.maps.Size(32, 32),
      },
    })
  }

  openDirections(destination: ParkingSpot): void {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${destination.latitude},${destination.longitude}&destination_place_id=${destination.place_id}`
    window.open(url, '_blank')
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371 // Radius of the Earth in kilometers
    const dLat = this.deg2rad(lat2 - lat1)
    const dLon = this.deg2rad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const d = R * c // Distance in kilometers
    return Math.round(d * 1000) // Convert to meters
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180)
  }
}

export const mapsService = new MapsService()
